// @ts-nocheck
/**
 * RarityEffectLayer - レアリティエフェクト層
 *
 * 子要素（CardV2等）にレアリティ別のビジュアルエフェクトを適用する
 * ホロ、パーティクル、光沢、マウス追従、二重枠、コーナー装飾を提供
 */

import React, { useState, useRef, useCallback, useEffect, memo } from 'react';
import { RARITY_COLORS } from '../../collection/data/constants';
import {
  EFFECT_LEVELS,
  RARITY_KEYFRAMES,
  applyRarityStyle,
  getRarityOverlay,
  getParticleCount,
  getParticleStyle,
  hasDoubleBorder,
  hasCornerOrnaments,
  calculateMouseReflection,
  getMouseFollowStyle,
  getMouseGlareStyle,
} from '../../styles/rarityEffects';

// ========================================
// 型定義
// ========================================

export interface RarityEffectLayerProps {
  /** 子要素（CardV2等） */
  children: React.ReactNode;

  /** レアリティ */
  rarity: string;

  /** エフェクトレベル */
  effectLevel?: 'full' | 'minimal' | 'off';

  /** カード幅（エフェクトサイズ計算用） */
  width: number;

  /** カード高さ（エフェクトサイズ計算用） */
  height: number;

  /** マウス追従エフェクト有効化 */
  enableMouseEffect?: boolean;

  /** クリックハンドラ */
  onClick?: () => void;

  /** ホバー時のtransform（scale等） */
  hoverTransform?: string;

  /** 追加のスタイル */
  style?: React.CSSProperties;

  /** 追加のクラス名 */
  className?: string;
}

// ========================================
// メインコンポーネント
// ========================================

const RarityEffectLayer: React.FC<RarityEffectLayerProps> = memo(({
  children,
  rarity,
  effectLevel = 'full',
  width,
  height,
  enableMouseEffect = true,
  onClick,
  hoverTransform = 'translateY(-4px) scale(1.02)',
  style,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [mouseReflection, setMouseReflection] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const lastMoveTime = useRef(0);

  // キーフレームをDOMに注入
  useEffect(() => {
    const styleId = 'rarity-effect-keyframes';
    if (!document.getElementById(styleId)) {
      const styleSheet = document.createElement('style');
      styleSheet.id = styleId;
      styleSheet.textContent = RARITY_KEYFRAMES;
      document.head.appendChild(styleSheet);
    }
  }, []);

  // レアリティカラー
  const rarityColor = RARITY_COLORS[rarity] || '#808080';

  // パーティクル数
  const particleCount = getParticleCount(rarity, effectLevel);

  // 二重枠チェック
  const showDoubleBorder = hasDoubleBorder(rarity, effectLevel);
  const showCornerOrnaments = hasCornerOrnaments(rarity, effectLevel);

  // ベーススタイル
  const baseStyle: React.CSSProperties = {
    width,
    height,
    borderRadius: '8px',
    overflow: 'hidden',
    cursor: onClick ? 'pointer' : 'default',
  };

  // レアリティエフェクト適用
  const containerStyle = applyRarityStyle(baseStyle, rarity, effectLevel);

  // オーバーレイスタイル
  const overlayStyle = getRarityOverlay(rarity, effectLevel);

  // マウス追従スタイル
  const mouseFollowStyle = enableMouseEffect
    ? getMouseFollowStyle(mouseReflection, rarity)
    : {};
  const mouseGlareStyle = enableMouseEffect
    ? getMouseGlareStyle(mouseReflection, rarity)
    : null;

  // ホバー時のtransform
  const hoverStyle = isHovered && hoverTransform
    ? { transform: hoverTransform }
    : {};

  // イベントハンドラ
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    setMouseReflection(null);
  }, []);

  // マウスムーブにthrottle処理（16ms = 約60fps）
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!enableMouseEffect || effectLevel !== 'full') return;

    const now = Date.now();
    if (now - lastMoveTime.current < 16) return;
    lastMoveTime.current = now;

    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const reflection = calculateMouseReflection(rect, e.clientX, e.clientY);
    setMouseReflection(reflection);
  }, [enableMouseEffect, effectLevel]);

  const handleClick = useCallback(() => {
    if (onClick) onClick();
  }, [onClick]);

  return (
    <div
      ref={containerRef}
      style={{
        ...containerStyle,
        ...mouseFollowStyle,
        ...hoverStyle,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        ...style,
      }}
      className={className}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
    >
      {/* 子要素（CardV2等） - 最下層 */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          zIndex: 1,
        }}
      >
        {children}
      </div>

      {/* 二重枠（UR, GR用） */}
      {showDoubleBorder && (
        <div
          style={{
            position: 'absolute',
            top: '3px',
            left: '3px',
            right: '3px',
            bottom: '3px',
            border: `1px solid ${rarityColor}80`,
            borderRadius: '6px',
            pointerEvents: 'none',
            zIndex: 2,
          }}
        />
      )}

      {/* コーナー装飾（GR用） */}
      {showCornerOrnaments && (
        <>
          <div
            style={{
              position: 'absolute',
              top: '-2px',
              left: '-2px',
              width: '16px',
              height: '16px',
              borderTop: `3px solid ${rarityColor}`,
              borderLeft: `3px solid ${rarityColor}`,
              borderRadius: '4px 0 0 0',
              zIndex: 10,
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: '-2px',
              right: '-2px',
              width: '16px',
              height: '16px',
              borderTop: `3px solid ${rarityColor}`,
              borderRight: `3px solid ${rarityColor}`,
              borderRadius: '0 4px 0 0',
              zIndex: 10,
            }}
          />
          <div
            style={{
              position: 'absolute',
              bottom: '-2px',
              left: '-2px',
              width: '16px',
              height: '16px',
              borderBottom: `3px solid ${rarityColor}`,
              borderLeft: `3px solid ${rarityColor}`,
              borderRadius: '0 0 0 4px',
              zIndex: 10,
            }}
          />
          <div
            style={{
              position: 'absolute',
              bottom: '-2px',
              right: '-2px',
              width: '16px',
              height: '16px',
              borderBottom: `3px solid ${rarityColor}`,
              borderRight: `3px solid ${rarityColor}`,
              borderRadius: '0 0 4px 0',
              zIndex: 10,
            }}
          />
        </>
      )}

      {/* レアリティオーバーレイ（光沢、ホロ等） */}
      {overlayStyle && <div style={overlayStyle} />}

      {/* マウス追従グレア */}
      {mouseGlareStyle && <div style={mouseGlareStyle} />}

      {/* パーティクル */}
      {Array.from({ length: particleCount }).map((_, i) => (
        <div key={i} style={getParticleStyle(i, rarity)} />
      ))}
    </div>
  );
});

RarityEffectLayer.displayName = 'RarityEffectLayer';

export default RarityEffectLayer;
